import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CampaignIcon from '@mui/icons-material/CampaignOutlined'
import MoreIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import {
  COULEURS_STATUT_CAMPAGNE,
  LIBELLES_SEGMENT,
  LIBELLES_STATUT_CAMPAGNE,
  STATUTS_CAMPAGNE,
  TYPES_SEGMENT,
  hasPermission,
  permissionsForRole,
  transitionPossible,
  type Campagne,
  type Role,
  type StatistiquesCampagnes,
  type StatutCampagne,
  type TypeSegment,
} from '@iobiz/shared'
import { ApiError, api } from '../api/client.js'
import { DataTable, EmptyState, type Column } from '../ui/DataTable.js'
import { KpiCard } from '../ui/KpiCard.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { CampagneDialog } from '../features/campagnes/CampagneDialog.js'
import { RapportDialog } from '../features/campagnes/RapportDialog.js'

/**
 * CAMPAGNES MARKETING — la même ergonomie que la page Clients : un titre, un
 * bouton d'action, une barre de filtres, un tableau.
 *
 * ⭐ Deux onglets et pas deux pages : « ce que j'ai envoyé » et « ce que ça a
 * donné » sont deux questions du même moment. Les séparer dans le menu aurait
 * produit un tableau de bord que personne n'ouvre.
 */

const dateCourte = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'

const dateHeure = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

interface Reglages {
  configure: boolean
  templateName: string | null
}

export function CampagnesPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const user = useSessionStore((s) => s.user)
  const storeId = store?.storeId

  /**
   * Les droits, calculés du rôle — comme côté serveur, à partir de la même
   * table. Un bouton visible qui répond 403 est pire qu'un bouton absent.
   */
  const droits = useMemo(() => {
    const p = store ? permissionsForRole(store.role as Role) : []
    const tous = user?.isPlatformAdmin ? [...p, 'platform:admin' as const] : p
    return {
      creer: hasPermission(tous, 'campaign:create'),
      modifier: hasPermission(tous, 'campaign:update'),
      piloter: hasPermission(tous, 'campaign:manage'),
      supprimer: hasPermission(tous, 'campaign:delete'),
    }
  }, [store, user])

  const [params, setParams] = useSearchParams()
  const [onglet, setOnglet] = useState(0)
  const [campagnes, setCampagnes] = useState<Campagne[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<StatistiquesCampagnes | null>(null)
  const [reglages, setReglages] = useState<Reglages | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const [formOuvert, setFormOuvert] = useState(false)
  const [enEdition, setEnEdition] = useState<Campagne | null>(null)
  const [rapportDe, setRapportDe] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ el: HTMLElement; campagne: Campagne } | null>(null)
  const [aSupprimer, setASupprimer] = useState<Campagne | null>(null)

  const recherche = params.get('q') ?? ''
  const statut = (params.get('statut') as StatutCampagne | null) ?? undefined
  const segment = (params.get('segment') as TypeSegment | null) ?? undefined
  const [saisie, setSaisie] = useState(recherche)

  const patch = useCallback(
    (changes: Record<string, string | undefined>) => {
      const next = new URLSearchParams(params)
      for (const [k, v] of Object.entries(changes)) {
        if (!v) next.delete(k)
        else next.set(k, v)
      }
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  // Anti-rebond : une frappe ne déclenche pas une requête.
  useEffect(() => {
    const t = setTimeout(() => {
      if (saisie !== recherche) patch({ q: saisie })
    }, 300)
    return () => clearTimeout(t)
  }, [saisie, recherche, patch])

  const charger = useCallback(async (): Promise<void> => {
    if (!storeId) return
    const q = new URLSearchParams({ limit: '100' })
    if (recherche) q.set('q', recherche)
    if (statut) q.set('statut', statut)
    if (segment) q.set('segment', segment)
    try {
      const [liste, s] = await Promise.all([
        api.get<{ data: Campagne[]; total: number }>(`/stores/${storeId}/campaigns?${q}`),
        api.get<StatistiquesCampagnes>(`/stores/${storeId}/campaigns/statistiques`),
      ])
      setCampagnes(liste.data)
      setTotal(liste.total)
      setStats(s)
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.')
    } finally {
      setChargement(false)
    }
  }, [storeId, recherche, statut, segment])

  useEffect(() => {
    void charger()
  }, [charger])

  useEffect(() => {
    if (!storeId) return
    api
      .get<Reglages>(`/stores/${storeId}/campaigns/reglages`)
      .then(setReglages)
      .catch(() => setReglages(null))
  }, [storeId])

  /**
   * ⭐ Rafraîchissement automatique TANT QU'UNE CAMPAGNE TOURNE, et pas
   * autrement. Un envoi avance par lots toutes les trente secondes : sans
   * cela, l'écran resterait figé sur « 0 envoyé » pendant que les messages
   * partent, et le gérant relancerait une campagne déjà en cours.
   */
  const enCours = campagnes.some((c) => c.status === 'running')
  useEffect(() => {
    if (!enCours) return
    const t = setInterval(() => void charger(), 10_000)
    return () => clearInterval(t)
  }, [enCours, charger])

  async function agir(campagne: Campagne, action: string, libelle: string): Promise<void> {
    if (!storeId) return
    setMenu(null)
    setErreur(null)
    try {
      await api.post(`/stores/${storeId}/campaigns/${campagne.id}/${action}`, {})
      setInfo(`${libelle} : « ${campagne.name} ».`)
      await charger()
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Action impossible.')
    }
  }

  /**
   * REJOUER : le serveur crée une COPIE en brouillon, qu'on ouvre aussitôt.
   *
   * ⭐ On n'ouvre pas l'originale : la reprise est une autre campagne, avec sa
   * propre date et ses propres résultats. Rafraîchir l'originale aurait laissé
   * croire qu'on venait de la relancer telle quelle.
   */
  async function rejouer(campagne: Campagne): Promise<void> {
    if (!storeId) return
    setMenu(null)
    setErreur(null)
    try {
      const copie = await api.post<Campagne>(
        `/stores/${storeId}/campaigns/${campagne.id}/reprise-nouvelle`,
        {},
      )
      await charger()
      setEnEdition(copie)
      setFormOuvert(true)
      setInfo(`Reprise créée : « ${copie.name} ». Vérifiez-la avant de la lancer.`)
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Reprise impossible.')
    }
  }

  /** ⚠️ Laisse REMONTER l'erreur : `ConfirmDialog` garde alors la boîte
   *  ouverte et affiche le motif, au lieu de se fermer sur un échec muet. */
  async function supprimer(): Promise<void> {
    if (!storeId || !aSupprimer) return
    await api.delete(`/stores/${storeId}/campaigns/${aSupprimer.id}`)
    await charger()
  }

  const colonnes: Column<Campagne>[] = [
    {
      key: 'name',
      label: 'Campagne',
      render: (c) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {c.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {c.internalSubject || LIBELLES_SEGMENT[c.segment.type]}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'createdAt',
      label: 'Créée le',
      render: (c) => <Typography variant="body2">{dateCourte(c.createdAt)}</Typography>,
    },
    {
      key: 'author',
      label: 'Créateur',
      render: (c) => (
        <Typography variant="body2" sx={{ color: c.createdByName ? undefined : 'text.disabled' }}>
          {c.createdByName ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Statut',
      render: (c) => (
        <Box>
          <Chip
            size="small"
            label={LIBELLES_STATUT_CAMPAGNE[c.status]}
            color={COULEURS_STATUT_CAMPAGNE[c.status]}
          />
          {/* La barre n'apparaît que pendant l'envoi : une progression figée à
              100 % sur une campagne terminée n'apprend rien. */}
          {c.status === 'running' && c.recipientsTotal > 0 && (
            <LinearProgress
              variant="determinate"
              value={((c.sentCount + c.failedCount) / c.recipientsTotal) * 100}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
          )}
        </Box>
      ),
    },
    {
      key: 'recipients',
      label: 'Destinataires',
      numeric: true,
      align: 'right',
      render: (c) => <span>{c.recipientsTotal}</span>,
    },
    {
      key: 'sent',
      label: 'Envoyés',
      numeric: true,
      align: 'right',
      render: (c) => <span>{c.sentCount}</span>,
    },
    {
      key: 'failed',
      label: 'Échecs',
      numeric: true,
      align: 'right',
      render: (c) => (
        <Typography
          variant="body2"
          className="tabular"
          sx={{ color: c.failedCount > 0 ? 'error.main' : 'text.disabled' }}
        >
          {c.failedCount}
        </Typography>
      ),
    },
    {
      key: 'sentAt',
      label: 'Date d’envoi',
      render: (c) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {c.sentAt ? dateHeure(c.sentAt) : c.scheduledAt ? `prévu ${dateHeure(c.scheduledAt)}` : '—'}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 48,
      render: (c) => (
        <IconButton
          size="small"
          aria-label={`Actions sur ${c.name}`}
          onClick={(e) => {
            e.stopPropagation()
            setMenu({ el: e.currentTarget, campagne: c })
          }}
        >
          <MoreIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ),
    },
  ]

  const active = menu?.campagne
  const filtresActifs = [statut, segment].filter(Boolean).length

  return (
    <Box>
      {erreur && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 4 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {/**
       * ⚠️ LE MODE SIMULATION SE DIT. Sans compte WhatsApp Business
       * configuré, tout fonctionne — segments, préparation, rapports — mais
       * aucun message ne part. Le taire produirait la pire des situations :
       * une campagne « terminée avec 100 % de réussite » que personne n'a
       * reçue.
       */}
      {reglages && !reglages.configure && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <strong>Mode simulation.</strong> Le canal WhatsApp n’est pas configuré : les campagnes se
          préparent et se déroulent normalement, mais aucun message ne part réellement. Renseignez le
          numéro expéditeur et le modèle approuvé dans les réglages pour envoyer pour de bon.
        </Alert>
      )}
      {reglages?.configure && !reglages.templateName && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Aucun <strong>modèle approuvé</strong> n’est renseigné. WhatsApp ne remet un message
          publicitaire que par un modèle validé par Meta : sans lui, les envois reviendront en échec.
        </Alert>
      )}

      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5, gap: 4 }}
      >
        <Box>
          <Typography variant="h1">Campagnes marketing</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {total} campagne{total > 1 ? 's' : ''}
            {filtresActifs > 0 && ` · ${filtresActifs} filtre${filtresActifs > 1 ? 's' : ''}`}
          </Typography>
        </Box>
        {droits.creer && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEnEdition(null)
              setFormOuvert(true)
            }}
          >
            Nouvelle campagne
          </Button>
        )}
      </Box>

      <Tabs value={onglet} onChange={(_, v: number) => setOnglet(v)} sx={{ mb: 4 }}>
        <Tab label="Campagnes" />
        <Tab label="Tableau de bord" />
      </Tabs>

      {onglet === 0 && (
        <>
          <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Nom, objet, message…"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Statut"
                value={statut ?? ''}
                onChange={(e) => patch({ statut: e.target.value })}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">Tous</MenuItem>
                {STATUTS_CAMPAGNE.map((s) => (
                  <MenuItem key={s} value={s}>
                    {LIBELLES_STATUT_CAMPAGNE[s]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Segment"
                value={segment ?? ''}
                onChange={(e) => patch({ segment: e.target.value })}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">Tous</MenuItem>
                {TYPES_SEGMENT.map((s) => (
                  <MenuItem key={s} value={s}>
                    {LIBELLES_SEGMENT[s]}
                  </MenuItem>
                ))}
              </TextField>
              {(recherche || filtresActifs > 0) && (
                <Button
                  onClick={() => {
                    setSaisie('')
                    setParams({}, { replace: true })
                  }}
                >
                  Réinitialiser
                </Button>
              )}
            </Box>
          </Paper>

          <DataTable
            columns={colonnes}
            rows={campagnes}
            rowKey={(c) => c.id}
            loading={chargement}
            total={total}
            /**
             * ⚠️ CORRIGÉ : la ligne ouvrait le RAPPORT. Elle répondait donc à
             * « qu'est-ce que ça a donné » quand la question qu'on se pose en
             * cliquant est « qu'est-ce que c'est, et qu'est-ce que j'en
             * fais ». La gestion s'ouvre ici, le rapport reste à un clic.
             */
            onRowClick={(c) => {
              setEnEdition(c)
              setFormOuvert(true)
            }}
            emptyState={
              <EmptyState
                icon={<CampaignIcon sx={{ fontSize: 40 }} />}
                title="Aucune campagne"
                description="Créez votre première campagne WhatsApp : choisissez qui vous voulez toucher, l’assistant vous aide à écrire le message."
                action={
                  droits.creer ? (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setEnEdition(null)
                        setFormOuvert(true)
                      }}
                    >
                      Nouvelle campagne
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </>
      )}

      {onglet === 1 && stats && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 4,
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            }}
          >
            <KpiCard label="Campagnes" value={String(stats.total)} />
            <KpiCard label="Envoyées ce mois" value={String(stats.envoyeesCeMois)} />
            <KpiCard label="Destinataires touchés" value={String(stats.destinatairesTotal)} />
            <KpiCard
              label="Taux de réussite"
              value={String(stats.tauxReussite)}
              unit="%"
              warning={stats.tauxReussite > 0 && stats.tauxReussite < 80}
              detail={
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {stats.messagesEnvoyes} remis · {stats.messagesEchoues} en échec
                </Typography>
              }
            />
          </Box>

          {(stats.enCours > 0 || stats.programmees > 0) && (
            <Alert severity="info">
              {stats.enCours > 0 && `${stats.enCours} campagne(s) en cours d’envoi. `}
              {stats.programmees > 0 && `${stats.programmees} campagne(s) programmée(s).`}
            </Alert>
          )}

          <Paper sx={{ p: 5, borderRadius: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 4 }}>
              Campagnes les plus performantes
            </Typography>
            {stats.meilleures.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Aucune campagne terminée d’au moins dix destinataires. Le classement apparaîtra
                lorsqu’il aura de quoi comparer.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {stats.meilleures.map((m) => (
                  <Box
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setRapportDe(m.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setRapportDe(m.id)
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {m.name}
                      </Typography>
                      <Typography variant="body2" className="tabular">
                        {m.tauxReussite} % · {m.sentCount}/{m.recipientsTotal}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={m.tauxReussite}
                      sx={{ height: 5, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 5, borderRadius: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 4 }}>
              Historique — douze derniers mois
            </Typography>
            {/* Un histogramme en CSS plutôt qu'une bibliothèque de graphiques :
                douze barres et un axe ne justifient pas 80 ko de dépendance. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 140 }}>
              {stats.historique.map((h) => {
                const max = Math.max(...stats.historique.map((x) => x.envoyes), 1)
                return (
                  <Box key={h.mois} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                    <Box
                      title={`${h.envoyes} messages · ${h.campagnes} campagne(s)`}
                      sx={{
                        height: `${Math.round((h.envoyes / max) * 100)}%`,
                        minHeight: h.envoyes > 0 ? 4 : 1,
                        bgcolor: h.envoyes > 0 ? 'primary.main' : 'divider',
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                      {h.mois.slice(5)}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Box>
      )}

      {/* --------------------------------------------------------- le menu */}
      <Menu anchorEl={menu?.el ?? null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (active) setRapportDe(active.id)
            setMenu(null)
          }}
        >
          Voir le rapport
        </MenuItem>

        {droits.creer && active && (
          <MenuItem onClick={() => void rejouer(active)}>Rejouer</MenuItem>
        )}

        {droits.modifier && active && ['draft', 'scheduled', 'failed'].includes(active.status) && (
          <MenuItem
            onClick={() => {
              setEnEdition(active)
              setFormOuvert(true)
              setMenu(null)
            }}
          >
            Modifier
          </MenuItem>
        )}

        {/* ⭐ Reprendre et lancer ne sont pas le même geste : le journal doit
            pouvoir dire « repris » là où c'est repris, sinon le rapport d'une
            campagne suspendue puis relancée montre deux départs. */}
        {droits.piloter && active && transitionPossible(active.status, 'running') && (
          <MenuItem
            onClick={() =>
              active.status === 'paused'
                ? void agir(active, 'reprise', 'Envoi repris')
                : void agir(active, 'lancement', 'Campagne lancée')
            }
          >
            {active.status === 'paused' ? 'Reprendre l’envoi' : 'Lancer maintenant'}
          </MenuItem>
        )}

        {/* ⭐ « Envoyer un lot » : la main du gérant sur le cycle de l'agent,
            pour vérifier tout de suite que ça part, sans attendre le prochain
            passage automatique. */}
        {droits.piloter && active?.status === 'running' && (
          <MenuItem onClick={() => void agir(active, 'avancement', 'Lot traité')}>
            Envoyer un lot maintenant
          </MenuItem>
        )}

        {droits.piloter && active && transitionPossible(active.status, 'paused') && (
          <MenuItem onClick={() => void agir(active, 'suspension', 'Campagne suspendue')}>
            Suspendre
          </MenuItem>
        )}

        {droits.piloter && active && transitionPossible(active.status, 'cancelled') && (
          <MenuItem
            sx={{ color: 'warning.main' }}
            onClick={() => void agir(active, 'annulation', 'Campagne annulée')}
          >
            Annuler
          </MenuItem>
        )}

        {droits.supprimer && active && active.status !== 'running' && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setASupprimer(active)
              setMenu(null)
            }}
          >
            Supprimer
          </MenuItem>
        )}
      </Menu>

      {formOuvert && storeId && (
        <CampagneDialog
          storeId={storeId}
          campagne={enEdition}
          onFermer={() => setFormOuvert(false)}
          onEnregistre={() => {
            setFormOuvert(false)
            void charger()
          }}
          onRapport={(id) => {
            setFormOuvert(false)
            setRapportDe(id)
          }}
          onAction={(c, action, libelle) => {
            setFormOuvert(false)
            void agir(c, action, libelle)
          }}
          onRejouer={(c) => void rejouer(c)}
        />
      )}

      {rapportDe && storeId && (
        <RapportDialog
          storeId={storeId}
          campagneId={rapportDe}
          onFermer={() => setRapportDe(null)}
        />
      )}

      {aSupprimer && (
        <ConfirmDialog
          open
          title="Supprimer cette campagne ?"
          message={`« ${aSupprimer.name} » et son rapport seront retirés de la liste.`}
          confirmLabel="Supprimer"
          onConfirm={supprimer}
          onClose={() => setASupprimer(null)}
        />
      )}
    </Box>
  )
}
